import React, { useState } from 'react';
import { Pill } from 'lucide-react';
import { anamnesiVociAPI } from '../../services/api';
import { oggiClinica } from '../../utils/oraItaliana';
import { annunciaAnamnesiCambiata } from '../../utils/annunci';
import { messaggioDa } from '../../utils/notifiche';

/**
 * TerapieInCorso — la sezione «Terapie in corso» dell'anamnesi, a tabella.
 *
 * Una riga = una voce della sezione «farmaci» (migration 233): lo stesso
 * meccanismo delle altre voci - autore, data, registro modifiche, cestino,
 * cancellazione selettiva - con tre colonne in piu'.
 *
 *   Farmaco   il testo della voce, obbligatorio;
 *   Dosaggio  testo libero, facoltativo. Nessun calcolo e nessun avviso:
 *             il gestionale registra, non giudica;
 *   Inizio    mese e anno, facoltativo, mai nel futuro (lo rifiuta il
 *             server e, sotto, il database);
 *   Stop      la strada «superata» che esiste gia': chi e quando. Togliendo
 *             la spunta la voce si riattiva.
 *
 * Una terapia sospesa RESTA nella tabella, in grigio e spuntata: chi legge
 * deve sapere che quel farmaco c'era e che e' stato sospeso, e da chi.
 *
 * Le voci scritte prima della tabella con piu' farmaci in un testo solo
 * portano il segno «da dividere»: le divide a mano il medico o
 * l'amministratore, nessuno script interpreta il testo.
 */

const soloData = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const meseAnno = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})/);
    return m ? `${m[2]}/${m[1]}` : '';
};

const VUOTA = { testo: '', dosaggio: '', inizio: '' };

const TerapieInCorso = ({ pazienteId, voci, modificabile, autoreDi, onCambiata, onErrore, onElimina }) => {
    const [nuova, setNuova] = useState(null);       // { testo, dosaggio, inizio }
    const [modifica, setModifica] = useState(null); // { id, testo, dosaggio, inizio }
    const [salvataggio, setSalvataggio] = useState(false);

    // Il mese massimo dell'input: quello di oggi IN ITALIA, non del browser
    // in UTC. Il server ricontrolla con oggi_clinica().
    const meseMassimo = oggiClinica().slice(0, 7);

    const esegui = async (azione, ripiego) => {
        onErrore(null);
        try {
            setSalvataggio(true);
            await azione();
            onCambiata();
            annunciaAnamnesiCambiata();
            return true;
        } catch (e) {
            onErrore(messaggioDa(e, ripiego));
            return false;
        } finally {
            setSalvataggio(false);
        }
    };

    const salvaNuova = async () => {
        if (!nuova?.testo?.trim()) return;
        const ok = await esegui(() => anamnesiVociAPI.crea(pazienteId, {
            sezione: 'farmaci',
            testo: nuova.testo,
            dosaggio: nuova.dosaggio,
            inizio: nuova.inizio,
        }), 'Errore nel salvataggio');
        if (ok) setNuova(null);
    };

    const salvaModifica = async () => {
        if (!modifica?.testo?.trim()) return;
        const ok = await esegui(() => anamnesiVociAPI.aggiorna(modifica.id, {
            testo: modifica.testo,
            dosaggio: modifica.dosaggio,
            inizio: modifica.inizio,
        }), 'Errore nella modifica');
        if (ok) setModifica(null);
    };

    const cambiaStop = (v, spunta) => esegui(
        () => (spunta ? anamnesiVociAPI.supera(v.id, null) : anamnesiVociAPI.riattiva(v.id)),
        'Errore nell\'operazione'
    );

    const chiHaSospeso = (v) => {
        if (v.superata_da_cognome) return `Dr. ${v.superata_da_cognome}`;
        const nome = [v.superata_utente_nome, v.superata_utente_cognome].filter(Boolean).join(' ');
        return nome || 'non registrato';
    };

    const campi = (stato, setStato) => (
        <>
            <td>
                <input
                    type="text"
                    className="av-ter-input"
                    value={stato.testo}
                    onChange={(e) => setStato({ ...stato, testo: e.target.value })}
                    placeholder="Farmaco"
                    aria-label="Farmaco"
                    autoFocus
                />
            </td>
            <td>
                <input
                    type="text"
                    className="av-ter-input"
                    value={stato.dosaggio}
                    onChange={(e) => setStato({ ...stato, dosaggio: e.target.value })}
                    placeholder="facoltativo"
                    aria-label="Dosaggio"
                    maxLength={200}
                />
            </td>
            <td>
                <input
                    type="month"
                    className="av-ter-input"
                    value={stato.inizio}
                    max={meseMassimo}
                    onChange={(e) => setStato({ ...stato, inizio: e.target.value })}
                    aria-label="Inizio (mese e anno)"
                    title="Mese e anno di inizio, facoltativo"
                />
            </td>
        </>
    );

    const pulsantiSalva = (onAnnulla, onSalva, etichetta, testo) => (
        <div className="av-azioni-modifica">
            <button className="av-btn-annulla" onClick={onAnnulla} disabled={salvataggio}>Annulla</button>
            <button className="av-btn-salva" onClick={onSalva} disabled={salvataggio || !testo.trim()}>
                {salvataggio ? 'Salvataggio…' : etichetta}
            </button>
        </div>
    );

    return (
        <div className="av-sezione">
            <div className="av-sezione-testa">
                <h3><Pill size={16} strokeWidth={1.8} aria-hidden="true" />Terapie in corso</h3>
                <button
                    className="av-btn-aggiungi"
                    onClick={() => setNuova({ ...VUOTA })}
                    disabled={!!nuova}
                >
                    ＋ Aggiungi
                </button>
            </div>

            {voci.length === 0 && !nuova ? (
                <p className="av-vuoto">Nessuna terapia registrata.</p>
            ) : (
                <table className="av-ter-tabella">
                    <colgroup>
                        <col className="av-ter-col-farmaco" />
                        <col className="av-ter-col-dosaggio" />
                        <col className="av-ter-col-inizio" />
                        <col className="av-ter-col-stop" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Farmaco</th>
                            <th>Dosaggio</th>
                            <th>Inizio</th>
                            <th className="av-ter-stop">Stop</th>
                        </tr>
                    </thead>
                    {voci.map((v) => {
                        const sospesa = v.stato === 'superata';

                        if (modifica?.id === v.id) {
                            return (
                                <tbody key={v.id} className="av-ter-voce av-ter-in-modifica">
                                    <tr>
                                        {campi(modifica, setModifica)}
                                        <td />
                                    </tr>
                                    <tr className="av-ter-piede">
                                        <td colSpan={4}>
                                          <div className="av-voce-piede">
                                            {v.da_dividere && (
                                                <span className="av-ter-dividere-aiuto">
                                                    Lascia qui un farmaco solo e aggiungi gli altri con «＋ Aggiungi».
                                                </span>
                                            )}
                                            {pulsantiSalva(() => setModifica(null), salvaModifica, 'Salva', modifica.testo)}
                                          </div>
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        }

                        return (
                            <tbody key={v.id} className={`av-ter-voce ${sospesa ? 'av-ter-sospesa' : ''}`}>
                                <tr>
                                    <td className="av-ter-farmaco">
                                        <span className="av-testo">{v.testo}</span>
                                        {v.da_dividere && (
                                            <span
                                                className="av-ter-dividere"
                                                title="Scritta prima della tabella, con più farmaci in una voce sola: va divisa, una riga per farmaco"
                                            >
                                                da dividere
                                            </span>
                                        )}
                                    </td>
                                    <td>{v.dosaggio || <span className="av-ter-vuoto">—</span>}</td>
                                    <td>{meseAnno(v.inizio_mese) || <span className="av-ter-vuoto">—</span>}</td>
                                    <td className="av-ter-stop">
                                        <input
                                            type="checkbox"
                                            checked={sospesa}
                                            disabled={salvataggio}
                                            onChange={(e) => cambiaStop(v, e.target.checked)}
                                            aria-label={sospesa ? 'Riattiva la terapia' : 'Sospendi la terapia'}
                                            title={sospesa ? 'Togli la spunta per riattivarla' : 'Spunta per segnare la terapia come sospesa'}
                                        />
                                    </td>
                                </tr>
                                <tr className="av-ter-piede">
                                    <td colSpan={4}>
                                      <div className="av-voce-piede">
                                        <span className="av-firma">
                                            {autoreDi(v)}
                                            {` · inserita il ${soloData(v.created_at) || '—'}`}
                                            {sospesa && (
                                                <span className="av-ter-sospesa-nota">
                                                    {` · sospesa · ${chiHaSospeso(v)} · ${soloData(v.superata_il) || '—'}`}
                                                </span>
                                            )}
                                        </span>
                                        <div className="av-voce-azioni">
                                            {!sospesa && modificabile(v) && (
                                                <button
                                                    className="av-link"
                                                    onClick={() => setModifica({
                                                        id: v.id,
                                                        testo: v.testo,
                                                        dosaggio: v.dosaggio || '',
                                                        inizio: v.inizio_mese || '',
                                                    })}
                                                >
                                                    Modifica
                                                </button>
                                            )}
                                            {modificabile(v) && (
                                                <button className="av-link av-link-pericolo" onClick={() => onElimina(v)}>
                                                    Elimina
                                                </button>
                                            )}
                                        </div>
                                      </div>
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                    {nuova && (
                        <tbody className="av-ter-voce av-ter-in-modifica">
                            <tr>
                                {campi(nuova, setNuova)}
                                <td />
                            </tr>
                            <tr className="av-ter-piede">
                                <td colSpan={4}>
                                  <div className="av-voce-piede">
                                    {pulsantiSalva(() => setNuova(null), salvaNuova, 'Aggiungi terapia', nuova.testo)}
                                  </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            )}
        </div>
    );
};

export default TerapieInCorso;
